import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import ManaCost from '../components/ManaCost';
import { useManaSymbols } from '../hooks/useManaSymbols';
import { getCardBadges } from '../components/CardGrid';
import './CardDetailPage.css';

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api`;

interface CardFace {
  faceOrder: number;
  name: string;
  manaCost: string;
  typeLine: string;
  oracleText: string;
  flavorText: string;
  power: string;
  toughness: string;
  loyalty: string;
  colors: string;
}

interface CardDetail {
  cardId: string;
  oracleId: string;
  name: string;
  setCode: string;
  setName: string;
  rarity: string;
  manaCost: string;
  typeLine: string;
  oracleText: string;
  flavorText: string;
  colors: string;
  layoutCode: string;
  borderColor: string;
  power: string;
  toughness: string;
  loyalty: string;
  artist: string;
  collectorNumber: string;
  releasedAt: string;
  tcgplayerId: number | null;
  arenaId: number | null;
  mtgoId: number | null;
  reserved: boolean;
  fullArt: boolean;
  promo: boolean;
  reprint: boolean;
  imageSmall: string;
  imageUrl: string;
  imageLarge: string;
  hasLocalImage: boolean;
  backImageSmall: string;
  backImageUrl: string;
  backImageLarge: string;
  price: number | null;
  priceFoil: number | null;
  qtyOwned: number;
  keywords: string[];
  frameEffects: string[];
  finishes: string[];
  promoTypes: string[];
  faces: CardFace[];
}

interface PricePoint {
  date: string;
  usd: number | null;
  usdFoil: number | null;
}

interface Printing {
  cardId: string;
  setCode: string;
  setName: string;
  setType: string;
  collectorNumber: string;
  releasedAt: string;
  rarity: string;
  artist: string;
  borderColor: string;
  price: number | null;
  priceFoil: number | null;
  hasLocalImage: boolean;
  imageSmall: string;
  qtyOwned: number;
  frameEffects: string[];
  finishes: string[];
  promoTypes: string[];
}

interface OwnedCopy {
  qtyRegular: number;
  qtyFoil: number;
  quantity: number;
  condition: string;
  notes: string;
  priceOverride: number | null;
}

const RARITY_COLORS: Record<string, string> = {
  mythic: '#e8954a', rare: '#c8a45a', uncommon: '#8ab4c8', common: '#9a9aaa',
};
const DFC_LAYOUTS = ['transform', 'modal_dfc', 'double_faced_token', 'reversible_card'];

function isFoilPrinting(collectorNumber: string): boolean {
  return collectorNumber?.includes('★') || collectorNumber?.endsWith('†');
}

// ─── PRICE CHART ─────────────────────────────────────────────────────────────

function PriceChart({ prices }: { prices: PricePoint[] }) {
  const [tooltip, setTooltip] = useState<{
    x: number; y: number; date: string; usd: number | null; usdFoil: number | null;
  } | null>(null);

  if (!prices.length) return null;

  const W = 560, H = 180;
  const padL = 52, padR = 20, padT = 16, padB = 32;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;

  const hasFoil    = prices.some(p => p.usdFoil != null);
  const hasNonFoil = prices.some(p => p.usd != null);

  const allVals = [...prices.map(p => p.usd), ...prices.map(p => p.usdFoil)]
    .filter((v): v is number => v != null);
  const minVal  = Math.max(0, Math.min(...allVals) * 0.9);
  const maxVal  = Math.max(...allVals) * 1.1 || 1;
  const range   = maxVal - minVal || 1;

  const xScale = (i: number) => padL + (i / Math.max(prices.length - 1, 1)) * chartW;
  const yScale = (v: number) => padT + chartH - ((v - minVal) / range) * chartH;

  const makePath = (key: 'usd' | 'usdFoil') => {
    const pts: { x: number; y: number }[] = [];
    prices.forEach((p, i) => { if (p[key] != null) pts.push({ x: xScale(i), y: yScale(p[key]!) }); });
    if (!pts.length) return '';
    return pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  };

  return (
    <div className="price-chart-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}>
        {Array.from({ length: 5 }, (_, i) => {
          const v = minVal + range * (i / 4);
          const y = yScale(v);
          return (
            <g key={i}>
              <line x1={padL} x2={W - padR} y1={y} y2={y} stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
              <text x={padL - 6} y={y + 4} textAnchor="end" fill="#505070" fontSize="10">
                ${v >= 100 ? v.toFixed(0) : v.toFixed(2)}
              </text>
            </g>
          );
        })}
        {prices.map((p, i) => (
          <text key={i} x={xScale(i)} y={H - 8} textAnchor="middle" fill="#505070" fontSize="10">
            {p.date.slice(5)}
          </text>
        ))}
        {hasFoil && (
          <>
            <path d={makePath('usdFoil')} fill="none" stroke="#8ab4c8" strokeWidth="1.5" strokeDasharray="4,3" opacity="0.7" />
            {prices.map((p, i) => p.usdFoil != null && (
              <circle key={`f${i}`} cx={xScale(i)} cy={yScale(p.usdFoil)} r="3"
                fill="#8ab4c8" stroke="#0a0c10" strokeWidth="1" style={{ cursor: 'pointer' }}
                onMouseEnter={() => setTooltip({ x: xScale(i), y: yScale(p.usdFoil!), date: p.date, usd: p.usd, usdFoil: p.usdFoil })}
                onMouseLeave={() => setTooltip(null)} />
            ))}
          </>
        )}
        {hasNonFoil && (
          <>
            <path d={makePath('usd')} fill="none" stroke="#c8a45a" strokeWidth="2.5" />
            {prices.map((p, i) => p.usd != null && (
              <g key={`n${i}`}>
                <circle cx={xScale(i)} cy={yScale(p.usd)} r="4"
                  fill="#c8a45a" stroke="#0a0c10" strokeWidth="1.5" />
                <circle cx={xScale(i)} cy={yScale(p.usd)} r="10"
                  fill="transparent" style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setTooltip({ x: xScale(i), y: yScale(p.usd!), date: p.date, usd: p.usd, usdFoil: p.usdFoil })}
                  onMouseLeave={() => setTooltip(null)} />
              </g>
            ))}
          </>
        )}
        {tooltip && (() => {
          const ttW = 115, ttH = tooltip.usdFoil != null ? 52 : 36;
          // Flip tooltip to left side if near right edge
          const ttX = tooltip.x + ttW + 14 > W
            ? tooltip.x - ttW - 10
            : tooltip.x + 10;
          const ttY = Math.max(tooltip.y - ttH - 4, padT);
          return (
            <g>
              <rect x={ttX} y={ttY} width={ttW} height={ttH} rx="5"
                fill="#0d1018" stroke="rgba(200,164,90,0.35)" strokeWidth="1" />
              <text x={ttX + 8} y={ttY + 14} fill="#9090b0" fontSize="10">{tooltip.date}</text>
              {tooltip.usd != null && (
                <text x={ttX + 8} y={ttY + 28} fill="#c8a45a" fontSize="12" fontWeight="bold">
                  ${tooltip.usd.toFixed(2)}
                </text>
              )}
              {tooltip.usdFoil != null && (
                <text x={ttX + 8} y={ttY + (tooltip.usd != null ? 44 : 28)} fill="#8ab4c8" fontSize="11">
                  ✦ ${tooltip.usdFoil.toFixed(2)}
                </text>
              )}
            </g>
          );
        })()}
      </svg>
      <div className="chart-legend">
        {hasNonFoil && (
          <span className="legend-item">
            <span className="legend-dot" style={{ background: '#c8a45a' }} />
            Non-foil
          </span>
        )}
        {hasFoil && (
          <span className="legend-item">
            <span className="legend-line-dashed" />
            Foil
          </span>
        )}
      </div>
    </div>
  );
}


function OracleText({ text }: { text: string }) {
  const symbols = useManaSymbols();
  if (!text) return null;

  // Split a line into alternating text and symbol tokens
  // e.g. "Pay {2}{T}: deal 1 damage" → ["Pay ", "{2}", "", "{T}", ": deal 1 damage"]
  function renderLine(line: string, lineIndex: number) {
    const parts = line.split(/(\{[^}]+\})/g);
    return (
      <p key={lineIndex}>
        {parts.map((part, i) => {
          if (part.match(/^\{[^}]+\}$/)) {
            const sym = symbols[part];
            if (sym?.imageUrl) {
              return (
                <img
                  key={i}
                  src={sym.imageUrl}
                  alt={sym.english ?? part}
                  title={sym.english ?? part}
                  width={16}
                  height={16}
                  style={{
                    display: 'inline-block',
                    verticalAlign: 'middle',
                    width: 16,
                    height: 16,
                    margin: '0 1px',
                    flexShrink: 0,
                  }}
                />
              );
            }
            // Symbol not in DB — render as styled text badge
            return (
              <span key={i} className="oracle-symbol-fallback" title={part}>
                {part}
              </span>
            );
          }
          return <span key={i}>{part}</span>;
        })}
      </p>
    );
  }

  return (
    <div className="oracle-text">
      {text.split('\n').map((line, i) => renderLine(line, i))}
    </div>
  );
}

function CopyId({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="copy-id-row">
      <span className="copy-id-label">{label}</span>
      <span className="copy-id-value" title={value}>
        {value.length > 36 ? value.slice(0, 8) + '...' + value.slice(-8) : value}
      </span>
      <button className={`copy-id-btn ${copied ? 'copied' : ''}`} onClick={copy}>
        {copied ? '✓' : 'Copy'}
      </button>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function CardDetailPage() {
  const { cardId }  = useParams<{ cardId: string }>();
  const navigate    = useNavigate();
  const location    = useLocation();

  // Respect the source page so back button returns correctly
  const from     = (location.state as { from?: string } | null)?.from || '/';
  const fromLabel = from === '/portfolio'            ? '← Portfolio'
                  : from === '/market'               ? '← Market'
                  : from === '/wishlist'             ? '← Wish List'
                  : from.match(/\/decks\/\d+\/build/) ? '← Deck Builder'
                  : from.match(/\/decks\/\d+/)        ? '← Deck'
                  : '← Cards';

  const [card,        setCard]        = useState<CardDetail | null>(null);
  const [prices,      setPrices]      = useState<PricePoint[]>([]);
  const [printings,   setPrintings]   = useState<Printing[]>([]);
  const [owned,       setOwned]       = useState<OwnedCopy | null>(null);
  const [flipped,     setFlipped]     = useState(false);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState<string | null>(null);
  const [rulings,     setRulings]     = useState<{source:string; publishedAt:string; comment:string}[]>([]);
  const [kwDefs,      setKwDefs]      = useState<Record<string, string>>({});
  const [showRulings, setShowRulings] = useState(false);
  const [qtyRegular,     setQtyRegular]     = useState(0);
  const [qtyFoil,        setQtyFoil]        = useState(0);
  const [qtySaving,      setQtySaving]      = useState(false);
  const [priceOverride,  setPriceOverride]  = useState<string>('');
  const [overrideSaving, setOverrideSaving] = useState(false);

  const saveQty = async (regular: number, foil: number) => {
    setQtySaving(true);
    try {
      await axios.put(`${API}/cards/${cardId}/owned`, { qtyRegular: regular, qtyFoil: foil });
      setOwned(prev => prev
        ? { ...prev, qtyRegular: regular, qtyFoil: foil, quantity: regular + foil }
        : (regular + foil > 0 ? { qtyRegular: regular, qtyFoil: foil, quantity: regular + foil, condition: '', notes: '', priceOverride: null } : null)
      );
    } catch (e) {
      console.error('Failed to save qty', e);
    } finally {
      setQtySaving(false);
    }
  };

  const saveOverride = async () => {
    setOverrideSaving(true);
    try {
      const val = priceOverride.trim() === '' ? null : parseFloat(priceOverride);
      await axios.put(`${API}/cards/${cardId}/owned`, { priceOverride: val });
      setOwned(prev => prev ? { ...prev, priceOverride: val } : null);
    } catch (e) {
      console.error('Failed to save price override', e);
    } finally {
      setOverrideSaving(false);
    }
  };

  useEffect(() => {
    if (!cardId) return;
    setLoading(true);
    setFlipped(false);
    setShowRulings(false);
    Promise.all([
      axios.get(`${API}/cards/${cardId}/detail`),
      axios.get(`${API}/cards/${cardId}/prices`),
      axios.get(`${API}/cards/${cardId}/printings`),
      axios.get(`${API}/cards/${cardId}/owned`),
    ]).then(([cardRes, priceRes, printRes, ownedRes]) => {
      setCard(cardRes.data);
      setPrices(priceRes.data);
      setPrintings(printRes.data);
      const ownedData = ownedRes.data;
      setOwned(ownedData?.quantity > 0 ? ownedData : null);
      setQtyRegular(ownedData?.qtyRegular ?? 0);
      setQtyFoil(ownedData?.qtyFoil ?? 0);
      setPriceOverride(ownedData?.priceOverride != null ? String(ownedData.priceOverride) : '');
      // Fetch rulings
      axios.get(`${API}/cards/${cardId}/rulings`)
        .then(r => setRulings(r.data))
        .catch(() => setRulings([]));
    }).catch(e => { setError('Failed to load card details'); console.error(e); })
    .finally(() => setLoading(false));
  }, [cardId]);

  // Load keyword definitions once
  useEffect(() => {
    axios.get(`${API}/keywords`)
      .then(r => {
        const map: Record<string, string> = {};
        r.data.forEach((k: any) => { map[k.keyword.toLowerCase()] = k.definition; });
        setKwDefs(map);
      })
      .catch(() => {});
  }, []);

  if (loading) return <div className="detail-loading"><div className="detail-spinner" /></div>;
  if (error || !card) return (
    <div className="detail-error">
      <p>{error || 'Card not found'}</p>
      <button onClick={() => navigate(from)}>{fromLabel}</button>
    </div>
  );

  const isDFC       = DFC_LAYOUTS.includes(card.layoutCode);
  const isFoil      = isFoilPrinting(card.collectorNumber);
  const frontImg    = card.imageLarge || card.imageUrl || card.imageSmall;
  const backImg     = card.backImageLarge || card.backImageUrl || card.backImageSmall;
  const currentImg  = flipped ? (backImg || frontImg) : frontImg;
  const rarityColor = RARITY_COLORS[card.rarity] || '#888';

  // For DFC cards, use per-face data when available
  const activeFace  = isDFC && card.faces?.length > 0
                    ? card.faces[flipped ? 1 : 0]
                    : null;
  const displayName       = activeFace?.name        || card.name;
  const displayManaCost   = activeFace?.manaCost     || card.manaCost;
  const displayTypeLine   = activeFace?.typeLine     || card.typeLine;
  const displayOracleText = activeFace?.oracleText   || card.oracleText;
  const displayFlavorText = activeFace?.flavorText   || card.flavorText;
  const displayPower      = activeFace?.power        || card.power;
  const displayToughness  = activeFace?.toughness    || card.toughness;
  const displayLoyalty    = activeFace?.loyalty      || card.loyalty;

  const isPT        = displayPower && displayToughness;
  const isLoyalty   = displayLoyalty && !isPT;
  const scryfallUrl    = `https://scryfall.com/card/${card.setCode}/${card.collectorNumber}`;
  const tcgUrl         = card.tcgplayerId ? `https://www.tcgplayer.com/product/${card.tcgplayerId}` : null;
  const scgUrl         = `https://starcitygames.com/search/?search_query=${encodeURIComponent(card.name)}`;
  const cardKingdomUrl = `https://www.cardkingdom.com/catalog/search?search=header&filter%5Bname%5D=${encodeURIComponent(card.name).replace(/%20/g, '+')}`;
  const displayPrice = card.price ?? card.priceFoil;

  return (
    <div className="card-detail-page">
      <button className="detail-back" onClick={() => navigate(from)}>
        {fromLabel}
      </button>

      <div className="detail-layout">

        {/* ── LEFT ─────────────────────────────────── */}
        <div className="detail-image-col">
          <div className="detail-image-wrap">
            <img src={currentImg} alt={card.name} className="detail-card-image" />
            {isDFC && backImg && (
              <button className="detail-flip-btn"
                onPointerDown={e => { e.preventDefault(); setFlipped(prev => !prev); }}
                title={flipped ? 'Show front' : 'Show back'}>↺</button>
            )}
          </div>

          <div className="detail-owned-editor">
            <div className="owned-editor-title">Collection{(qtySaving || overrideSaving) && <span className="owned-saving">saving…</span>}</div>
            <div className="owned-editor-row">
              <span className="owned-editor-label">Regular</span>
              <div className="owned-qty-control">
                <button className="owned-qty-btn" onClick={() => { const v = Math.max(0, qtyRegular - 1); setQtyRegular(v); saveQty(v, qtyFoil); }}>−</button>
                <span className="owned-qty-value">{qtyRegular}</span>
                <button className="owned-qty-btn" onClick={() => { const v = qtyRegular + 1; setQtyRegular(v); saveQty(v, qtyFoil); }}>+</button>
              </div>
            </div>
            <div className="owned-editor-row">
              <span className="owned-editor-label">✦ Foil</span>
              <div className="owned-qty-control">
                <button className="owned-qty-btn" onClick={() => { const v = Math.max(0, qtyFoil - 1); setQtyFoil(v); saveQty(qtyRegular, v); }}>−</button>
                <span className="owned-qty-value foil">{qtyFoil}</span>
                <button className="owned-qty-btn" onClick={() => { const v = qtyFoil + 1; setQtyFoil(v); saveQty(qtyRegular, v); }}>+</button>
              </div>
            </div>
            <div className="owned-editor-divider" />
            <div className="owned-editor-row">
              <span className="owned-editor-label" title="Override the market price for portfolio calculations. Leave blank to use the latest Scryfall price.">
                Price Override
              </span>
              <div className="owned-override-control">
                <span className="owned-override-currency">$</span>
                <input
                  className="owned-override-input"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="—"
                  value={priceOverride}
                  onChange={e => setPriceOverride(e.target.value)}
                  onBlur={saveOverride}
                  onKeyDown={e => { if (e.key === 'Enter') saveOverride(); }}
                />
                {priceOverride && (
                  <button className="owned-override-clear" onClick={() => { setPriceOverride(''); saveOverride(); }} title="Clear override">✕</button>
                )}
              </div>
            </div>
            {owned?.notes && <div className="owned-notes">{owned.notes}</div>}
          </div>

          {displayPrice != null && displayPrice > 0 && (
            <div className="detail-price-badge">
              <span className="price-label">Market Price {isFoil ? '(Foil)' : ''}</span>
              <span className="price-value">${Number(displayPrice).toFixed(2)}</span>
            </div>
          )}
        </div>

        {/* ── RIGHT ────────────────────────────────── */}
        <div className="detail-info-col">

          <div className="detail-header">
            <div className="detail-name-row">
              <h1 className="detail-name">{displayName}</h1>
              <ManaCost manaCost={displayManaCost} size={20} />
            </div>
            <div className="detail-meta">
              <span className="detail-type">{displayTypeLine}</span>
              <span className="detail-rarity" style={{ color: rarityColor, borderColor: rarityColor }}>
                {card.rarity}
              </span>
              {card.reserved && <span className="detail-badge rl-badge">Reserved List</span>}
              {isFoil       && <span className="detail-badge foil-badge-header">✦ Foil</span>}
              {card.fullArt && <span className="detail-badge">Full Art</span>}
              {card.promo   && <span className="detail-badge">Promo</span>}
              {getCardBadges({ borderColor: card.borderColor, frameEffects: card.frameEffects, finishes: card.finishes, promoTypes: card.promoTypes }).map((b, i) => (
                <span key={i} className={`frame-badge ${b.className}`}>{b.label}</span>
              ))}
            </div>
          </div>

          {displayOracleText && (
            <div className="detail-section"><OracleText text={displayOracleText} /></div>
          )}
          {displayFlavorText && (
            <div className="detail-section detail-flavour"><p>{displayFlavorText.replace(/^"+|"+$/g, '')}</p></div>
          )}
          {isPT && (
            <div className="detail-stat-wrap">
              <div className="detail-stat-label">Power / Toughness</div>
              <span className="detail-pt">{displayPower}/{displayToughness}</span>
            </div>
          )}
          {isLoyalty && (
            <div className="detail-stat-wrap">
              <div className="detail-stat-label">Loyalty</div>
              <span className="detail-loyalty">{displayLoyalty}</span>
            </div>
          )}

          {/* ── KEYWORDS ────────────────────────────── */}
          {card.keywords && card.keywords.length > 0 && (
            <div className="detail-section">
              <div className="section-title">Keywords</div>
              <div className="keyword-pills">
                {card.keywords.map(kw => {
                  const def = kwDefs[kw.toLowerCase()];
                  return (
                    <div key={kw} className="keyword-pill" title={def || kw}>
                      {kw}
                      {def && <div className="keyword-tooltip">{def}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── RULINGS ─────────────────────────────── */}
          {rulings.length > 0 && (
            <div className="detail-section">
              <div className="section-title ruling-toggle"
                   onClick={() => setShowRulings(v => !v)}
                   style={{ cursor: 'pointer' }}>
                Rulings ({rulings.length})
                <span className="ruling-chevron">{showRulings ? '▲' : '▼'}</span>
              </div>
              {showRulings && (
                <div className="rulings-list">
                  {rulings.map((r, i) => (
                    <div key={i} className="ruling-row">
                      <div className="ruling-date">{r.publishedAt}</div>
                      <div className="ruling-text">{r.comment}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="detail-section detail-set-info">
            <div className="set-info-row">
              <span className="set-info-label">Set</span>
              <span className="set-info-value">{card.setName} ({card.setCode.toUpperCase()})</span>
            </div>
            <div className="set-info-row">
              <span className="set-info-label">Number</span>
              <span className="set-info-value">{card.collectorNumber}</span>
            </div>
            <div className="set-info-row">
              <span className="set-info-label">Released</span>
              <span className="set-info-value">{card.releasedAt}</span>
            </div>
            <div className="set-info-row">
              <span className="set-info-label">Artist</span>
              <span className="set-info-value">{card.artist}</span>
            </div>
          </div>

          <div className="detail-section">
            <div className="section-title">External Links</div>
            <div className="external-links">
              <a href={scryfallUrl} target="_blank" rel="noreferrer" className="ext-link tcg-link">
                <span className="ext-link-icon">⬡</span> Scryfall
              </a>
              {tcgUrl && (
                <a href={tcgUrl} target="_blank" rel="noreferrer" className="ext-link tcg-link">
                  <span className="ext-link-icon">🛒</span> TCGPlayer
                </a>
              )}
              <a href={scgUrl} target="_blank" rel="noreferrer" className="ext-link tcg-link">
                <span className="ext-link-icon">⭐</span> Star City Games
              </a>
              <a href={cardKingdomUrl} target="_blank" rel="noreferrer" className="ext-link tcg-link">
                <span className="ext-link-icon">♔</span> Card Kingdom
              </a>
              {card.mtgoId  && <span className="ext-link-plain">MTGO ID: {card.mtgoId}</span>}
              {card.arenaId && <span className="ext-link-plain">Arena ID: {card.arenaId}</span>}
            </div>
          </div>

          <div className="detail-section">
            <div className="section-title">Card IDs</div>
            <div className="ids-section">
              <CopyId label="Card ID"    value={card.cardId} />
              <CopyId label="Oracle ID"  value={card.oracleId} />
              {card.tcgplayerId && <CopyId label="TCGPlayer ID" value={String(card.tcgplayerId)} />}
            </div>
          </div>

          {prices.length > 0 && (
            <div className="detail-section">
              <div className="section-title">
                Price History
                <span className="section-subtitle">— {card.setName}</span>
              </div>
              <PriceChart prices={prices} />
            </div>
          )}

          {printings.length > 0 && (
            <div className="detail-section">
              <div className="section-title">
                All Printings
                <span className="section-count">{printings.length}</span>
              </div>
              <div className="printings-table">
                <div className="printings-header">
                  <span>Set</span>
                  <span>#</span>
                  <span>Released</span>
                  <span>R</span>
                  <span>Price</span>
                  <span>Foil</span>
                  <span>Owned</span>
                </div>
                {printings.map(p => {
                  const pIsFoil = isFoilPrinting(p.collectorNumber);
                  return (
                    <button key={p.cardId}
                      className={`printing-row ${p.cardId === cardId ? 'current' : ''}`}
                      onClick={() => navigate(`/cards/${p.cardId}`, { state: { from } })}>
                      <span className="printing-set">
                        <span className="printing-set-code">{p.setCode.toUpperCase()}</span>
                        <span className="printing-set-name">
                          {p.setName}
                          {pIsFoil && <span className="foil-badge-inline">✦</span>}
                          {getCardBadges({ borderColor: p.borderColor, frameEffects: p.frameEffects, finishes: p.finishes, promoTypes: p.promoTypes }).map((b, i) => (
                            <span key={i} className={`frame-badge ${b.className}`}>{b.label}</span>
                          ))}
                        </span>
                      </span>
                      <span className="printing-num">{p.collectorNumber}</span>
                      <span className="printing-date">{p.releasedAt?.slice(0, 7)}</span>
                      <span className="printing-rarity" style={{ color: RARITY_COLORS[p.rarity] || '#888' }}>
                        {p.rarity.charAt(0).toUpperCase()}
                      </span>
                      <span className="printing-price">
                        {p.price ? `$${Number(p.price).toFixed(2)}` : '—'}
                      </span>
                      <span className="printing-price printing-foil-price">
                        {p.priceFoil ? `$${Number(p.priceFoil).toFixed(2)}` : '—'}
                      </span>
                      <span className="printing-owned">
                        {p.qtyOwned > 0 ? `×${p.qtyOwned}` : ''}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
